import { useState, useEffect } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Users,
  Search,
  Filter,
  Phone,
  Mail,
  MapPin,
  GraduationCap,
  Calendar,
  Flame,
  ThermometerSun,
  Snowflake,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  TrendingUp,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const priorityConfig = {
  hot: { label: 'Hot', icon: Flame, color: 'bg-red-500 text-white', textColor: 'text-red-600' },
  warm: { label: 'Warm', icon: ThermometerSun, color: 'bg-amber-500 text-white', textColor: 'text-amber-600' },
  cold: { label: 'Cold', icon: Snowflake, color: 'bg-blue-400 text-white', textColor: 'text-blue-500' }
};

const statusConfig = {
  new: { label: 'New', icon: Clock, color: 'bg-ux-cyan text-white' },
  contacted: { label: 'Contacted', icon: Phone, color: 'bg-ux-violet text-white' },
  qualified: { label: 'Qualified', icon: CheckCircle, color: 'bg-green-500 text-white' },
  converted: { label: 'Converted', icon: TrendingUp, color: 'bg-emerald-600 text-white' },
  lost: { label: 'Lost', icon: XCircle, color: 'bg-gray-500 text-white' }
};

const LeadManagementPage = () => {
  const { token, logout } = useCMSAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDestination, setFilterDestination] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterDestination !== 'all') params.append('destination', filterDestination);
      
      const res = await fetch(`${API_URL}/api/leads?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch (e) {
      console.error('Failed to fetch leads:', e);
      toast.error('Failed to load leads');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [token, filterPriority, filterStatus, filterDestination]);

  const updateLeadStatus = async (leadId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        toast.success('Lead updated successfully');
        fetchLeads();
        fetchStats();
      } else {
        toast.error('Failed to update lead');
      }
    } catch (e) {
      console.error('Failed to update lead:', e);
      toast.error('Failed to update lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.phone?.includes(query) ||
      lead.destination?.toLowerCase().includes(query)
    );
  });

  const uniqueDestinations = [...new Set(leads.map(l => l.destination).filter(Boolean))];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64" data-testid="leads-loading">
          <div className="animate-spin w-8 h-8 border-4 border-ux-cyan border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="lead-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lead Management</h1>
            <p className="text-gray-500">
              Manage and track all incoming student inquiries
            </p>
          </div>
          <Button 
            onClick={() => { fetchLeads(); fetchStats(); }}
            variant="outline"
            className="gap-2"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-ux-cyan/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-ux-cyan" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">New Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.today || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Hot Leads</p>
                  <p className="text-2xl font-bold text-red-700">{stats?.hot || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.new || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Top Destination</p>
                  <p className="text-lg font-bold text-gray-900 truncate uppercase">
                    {stats?.by_destination?.[0]?._id || 'N/A'}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-ux-blue/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-ux-blue" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                  data-testid="search-leads-input"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-[140px] bg-white" data-testid="filter-priority">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px] bg-white" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterDestination} onValueChange={setFilterDestination}>
                  <SelectTrigger className="w-[160px] bg-white" data-testid="filter-destination">
                    <SelectValue placeholder="Destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Destinations</SelectItem>
                    {uniqueDestinations.map(dest => (
                      <SelectItem key={dest} value={dest}>{dest}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-2 border-b">
            <CardTitle className="text-lg flex items-center gap-2 text-gray-900">
              <Users className="w-5 h-5 text-ux-cyan" />
              All Leads ({filteredLeads.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="no-leads">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No leads found</p>
                <p className="text-sm">Leads will appear here when students submit inquiries</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leads-table">
                  <thead className="bg-gray-50">
                    <tr className="border-b text-left">
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Lead</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Contact</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Interest</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Priority</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Status</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Date</th>
                      <th className="px-4 py-3 font-medium text-gray-600 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => {
                      const priority = priorityConfig[lead.priority] || priorityConfig.cold;
                      const status = statusConfig[lead.status] || statusConfig.new;
                      const PriorityIcon = priority.icon;
                      const StatusIcon = status.icon;

                      return (
                        <tr 
                          key={lead.id} 
                          className="border-b last:border-0 hover:bg-gray-50 transition-colors"
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{lead.name}</p>
                              <p className="text-sm text-gray-500">{lead.source || 'Website'}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                <a href={`mailto:${lead.email}`} className="hover:text-ux-cyan">
                                  {lead.email}
                                </a>
                              </div>
                              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                                <a href={`tel:${lead.phone}`} className="hover:text-ux-cyan">
                                  {lead.phone}
                                </a>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              {lead.destination && (
                                <div className="flex items-center gap-1.5 text-sm">
                                  <MapPin className="w-3.5 h-3.5 text-ux-blue" />
                                  <span className="font-medium text-gray-900 uppercase">{lead.destination}</span>
                                </div>
                              )}
                              {lead.study_level && (
                                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                  <GraduationCap className="w-3.5 h-3.5" />
                                  <span className="capitalize">{lead.study_level}</span>
                                </div>
                              )}
                              {lead.intake_timeline && (
                                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                  <Calendar className="w-3.5 h-3.5" />
                                  <span className="capitalize">{lead.intake_timeline.replace('-', ' ')}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <Badge className={`${priority.color} gap-1`}>
                              <PriorityIcon className="w-3 h-3" />
                              {priority.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-4">
                            <Select
                              value={lead.status || 'new'}
                              onValueChange={(value) => updateLeadStatus(lead.id, { status: value })}
                            >
                              <SelectTrigger className="w-[130px] h-8 bg-white">
                                <div className="flex items-center gap-1.5">
                                  <StatusIcon className="w-3.5 h-3.5" />
                                  <span>{status.label}</span>
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusConfig).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>
                                    <div className="flex items-center gap-2">
                                      <config.icon className="w-3.5 h-3.5" />
                                      {config.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {formatDate(lead.created_at)}
                          </td>
                          <td className="px-4 py-4">
                            <Button
                              size="sm"
                              variant={lead.contacted ? "outline" : "default"}
                              className={lead.contacted ? "text-green-600 border-green-200" : "bg-ux-cyan hover:bg-ux-cyan/90 text-white"}
                              onClick={() => updateLeadStatus(lead.id, { contacted: !lead.contacted })}
                              data-testid={`mark-contacted-${lead.id}`}
                            >
                              {lead.contacted ? '✓ Contacted' : 'Mark Contacted'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default LeadManagementPage;
